import { defineResource } from '@tnn/core'
import { z } from 'zod'
import { bankAccountNumber, banks } from '../party'
import { keepable, keepableSignatureMethods, keptSignatureMethods } from '../signature'
import { diligenceKinds } from './diligence'
import { employmentTypes, nameTitles } from './resources'

/**
 * Job applications (ใบสมัครงาน): what someone joining fills in once — who they are, their address and who to call, the
 * bank their pay goes to, photos of their papers — so HR does not type it in. They fill it on their phone from an
 * invite link or their branch's join code (with the account they will sign in with), or their lead fills it for them
 * in HR. HR checks it, corrects what needs it, picks their shift and diligence rates and approves it: their employee
 * record and private details are written from it, and payroll finds their bank account when it sets their pay.
 */

export const applicationStatuses = ['pending', 'approved', 'rejected'] as const
export type ApplicationStatus = (typeof applicationStatuses)[number]
/** `invite`: by an invite link; `signup`: by a branch's join code; `lead`: filled in by their lead (or HR) */
export const applicationSources = ['invite', 'signup', 'lead'] as const

/** The papers asked for, a photo (or PDF) each: the ID card, the house registration, the bank book's first page, a driving licence */
export const applicationDocuments = ['idCardFile', 'houseFile', 'bankBookFile', 'licenceFile'] as const
export type ApplicationDocument = (typeof applicationDocuments)[number]

/** A Thai ID number's check digit: the 13th is (11 − Σ digit × (13 − position)) mod 11, mod 10 */
export function thaiIdValid(id: string): boolean {
  if (!/^\d{13}$/.test(id)) return false
  const sum = [...id.slice(0, 12)].reduce((total, digit, i) => total + Number(digit) * (13 - i), 0)
  return (11 - (sum % 11)) % 10 === Number(id[12])
}

const idCard = z
  .string()
  .trim()
  .regex(/^\d{13}$/, 'เลขบัตรประชาชนต้องเป็นตัวเลข 13 หลัก')
  .refine(thaiIdValid, 'เลขบัตรประชาชนไม่ถูกต้อง ตรวจอีกครั้ง')

/** An applicant signs as a signature kept as one's own is made (theirs becomes it): photographed on paper */
const photographedSignature = 'ลายเซ็นในใบสมัครต้องเป็นรูปถ่ายลายเซ็นบนกระดาษ'

/** What the person joining fills in (the public pages send these; HR's form has them too) */
export const applicantShape = {
  /** A photo of their face, from the front: their photo on the staff record and their avatar once approved */
  photoFile: z.string({ error: 'กรุณาถ่ายรูปหน้าตรง' }).min(1, 'กรุณาถ่ายรูปหน้าตรง'),
  title: z.enum(nameTitles),
  name: z.string().trim().min(1).max(120),
  nickname: z.string().trim().max(40).nullish(),
  phone: z.string().trim().min(9).max(20),
  idCard,
  birthDate: z.iso.date(),
  address: z.string().trim().min(1).max(500),
  emergencyName: z.string().trim().min(1).max(120),
  emergencyPhone: z.string().trim().min(9).max(30),
  emergencyRelation: z.string().trim().min(1).max(60),
  /** The day they can start (HR may change it) */
  startDate: z.iso.date().nullish(),
  bank: z.enum(banks).nullish(),
  bankAccount: bankAccountNumber,
  bankAccountName: z.string().trim().max(120).nullish(),
  idCardFile: z.string().nullish(),
  houseFile: z.string().nullish(),
  bankBookFile: z.string().nullish(),
  licenceFile: z.string().nullish(),
  /** Their signature under it (a PNG): kept as their signature in the system */
  signatureFile: z.string().nullish(),
  signatureMethod: z.enum(keptSignatureMethods).nullish(),
}
export const applicantSchema = z.object(applicantShape)
export type Applicant = z.infer<typeof applicantSchema>
/** As the applicant sends it themselves: signed */
export const signedApplicantSchema = applicantSchema.extend({
  signatureFile: z.string({ error: 'กรุณาเซ็นชื่อ' }).min(1, 'กรุณาเซ็นชื่อ'),
  signatureMethod: z.enum(keepableSignatureMethods, { error: photographedSignature }),
})

/** A signature put on an application (one HR or a lead fills in too) is a photographed one (422 at `signatureFile`) */
export const applicationSignatureIssue = (method: unknown) => (keepable(method) ? null : photographedSignature)

/**
 * hr.application: one person's application. Through the API a lead or HR fills one in (`create`, in their branch's
 * scope); the public pages make the others. `update` while it waits; `approve` (HR) approves it, once it has a shift,
 * or rejects it.
 */
export const application = defineResource({
  app: 'hr',
  name: 'application',
  path: 'applications',
  schema: z.object({
    ...applicantShape,
    branchId: z.string().min(1),
    positionId: z.string().nullish(),
    /** HR's: how they are employed (default by the position's people, else permanent) */
    employmentType: z.enum(employmentTypes).nullish(),
    /** HR's: the shift they work (hr.schedule), picked before they are taken on (approving without one: 422) */
    scheduleId: z.string().nullish(),
    /** HR's: the diligence allowance's rates (เบี้ยขยัน) by where they live; empty: none */
    diligence: z.enum(diligenceKinds).nullish(),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    photoFile: { type: 'file', list: false, form: { span: 12, custom: true }, import: false, export: false },
    title: { list: false, form: { span: 3 }, optionLabels: 'hr:profile.options.title' },
    name: { search: true, list: { priority: 1 }, form: { span: 6 } },
    nickname: { search: true, list: { priority: 2 }, form: { span: 3 } },
    phone: { type: 'phone', search: true, list: { priority: 2 }, form: { span: 4 } },
    idCard: { type: 'taxId', list: false, form: { span: 4 } },
    birthDate: { list: false, form: { span: 4 } },
    address: { type: 'textarea', list: false, form: { span: 12, section: 'contact' } },
    emergencyName: { list: false, form: { span: 4, section: 'contact' } },
    emergencyPhone: { type: 'phone', list: false, form: { span: 4, section: 'contact' } },
    emergencyRelation: { list: false, form: { span: 4, section: 'contact' } },
    branchId: {
      type: 'ref',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      filter: true,
      list: { priority: 1 },
      form: { span: 6, section: 'work' },
    },
    positionId: {
      type: 'ref',
      ref: { resource: 'hr.position', where: { status: 'active' } },
      filter: true,
      list: { priority: 1 },
      form: { span: 6, section: 'work' },
    },
    startDate: { filter: true, list: { width: 120, priority: 2 }, form: { span: 6, section: 'work' } },
    employmentType: { list: false, form: { span: 6, section: 'work' }, optionLabels: 'hr:employee.options.employmentType' },
    scheduleId: {
      type: 'ref',
      ref: { resource: 'hr.schedule', where: { status: 'active' } },
      list: false,
      form: { span: 6, section: 'work', custom: true },
    },
    diligence: { list: false, form: { span: 6, section: 'work', custom: true }, optionLabels: 'hr:employee.options.diligence' },
    bank: { type: 'select', options: banks, optionLabels: 'banks', list: false, form: { span: 4, section: 'bank' } },
    bankAccount: { list: false, form: { span: 4, section: 'bank' } },
    bankAccountName: { list: false, form: { span: 4, section: 'bank' } },
    idCardFile: { type: 'file', list: false, form: { span: 6, section: 'documents' }, import: false, export: false },
    houseFile: { type: 'file', list: false, form: { span: 6, section: 'documents' }, import: false, export: false },
    bankBookFile: { type: 'file', list: false, form: { span: 6, section: 'documents' }, import: false, export: false },
    licenceFile: { type: 'file', list: false, form: { span: 6, section: 'documents' }, import: false, export: false },
    signatureFile: { type: 'file', list: false, form: { span: 12, section: 'documents', custom: true }, import: false, export: false },
    signatureMethod: {
      type: 'select',
      options: keptSignatureMethods,
      list: false,
      form: { section: 'documents', hidden: true },
      import: false,
      export: false,
    },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'documents' } },
    status: {
      readOnly: true,
      type: 'select',
      options: applicationStatuses,
      filter: true,
      tones: { pending: 'warning', approved: 'success', rejected: 'neutral' },
      list: { width: 120, priority: 1 },
    },
    source: { readOnly: true, type: 'select', options: applicationSources, filter: true, list: { width: 130, priority: 2 } },
    submittedAt: { readOnly: true, type: 'datetime', filter: true, list: { width: 160, priority: 1 } },
    submittedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    /** Everyone who changed the bank account in HR after it was sent: someone else checks it against the bank book */
    bankChangedBy: { readOnly: true, type: 'multiselect', ref: { resource: 'admin.user' }, list: false },
    /** The account they joined with (invite, sign-up) */
    userId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: false },
    /** Their employee record: drafted when they joined by an invite, written when approved */
    employeeId: { readOnly: true, type: 'ref', ref: { resource: 'hr.employee' }, list: { priority: 3, hidden: true } },
    /** A sign-up's request for an account, decided in the requests */
    requestId: { readOnly: true, type: 'ref', ref: { resource: 'access.request' }, list: false },
    reviewedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    reviewedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    rejectReason: { readOnly: true, list: false },
  },
  title: 'name',
  columns: ['name', 'nickname', 'positionId', 'branchId', 'startDate', 'source', 'submittedAt', 'status'],
  sections: ['main', 'contact', 'work', 'bank', 'documents'],
  defaultSort: '-submittedAt',
  branchFields: ['branchId'],
  actions: ['read', 'create', 'update', 'approve', 'export'],
  card: { title: 'name', subtitle: 'positionId', meta: ['branchId', 'submittedAt'], status: 'status' },
})
